import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { ExchangeTradingSession } from '../proto/generated/trading-calendar'
import { selectTradingCalendarDataByExchange } from '../selectors/chart'
import { getFutureTradingSessionBoundaries, isWithinChartLiveTradingHours } from '../util/tradingSessionLive'
import { TradingSessionSecurityRef } from '../util/tradingSessionSelection'

/**
 * Whether the security is in a streaming session window now, recomputed on session boundaries
 * (aligned with market status / trading calendar data). Selection: exchange → segment → scope.
 */
export function useExchangeLiveTradingWindow(
    exchangeTradingSessions: ExchangeTradingSession[] | undefined,
    security: TradingSessionSecurityRef | undefined
): boolean {
    const calendarData = useSelector(selectTradingCalendarDataByExchange(security?.exchange))
    const [live, setLive] = useState(false)
    const [boundaryTick, setBoundaryTick] = useState(0)

    useEffect(() => {
        if (!exchangeTradingSessions?.length || security?.exchange === undefined) {
            setLive(false)
            return
        }

        const recompute = () => {
            setLive(isWithinChartLiveTradingHours(exchangeTradingSessions, security, Date.now(), calendarData))
        }

        recompute()

        const now = Date.now()
        const timeouts: ReturnType<typeof setTimeout>[] = []
        for (const boundary of getFutureTradingSessionBoundaries(
            exchangeTradingSessions,
            security,
            now,
            calendarData
        )) {
            const delay = boundary - now
            if (delay > 0) {
                timeouts.push(
                    setTimeout(() => {
                        recompute()
                        setBoundaryTick((t) => t + 1)
                    }, delay)
                )
            }
        }

        const interval = setInterval(recompute, 60_000)

        return () => {
            timeouts.forEach(clearTimeout)
            clearInterval(interval)
        }
    }, [
        exchangeTradingSessions,
        calendarData,
        security?.exchange,
        security?.segment,
        security?.partOfCAS,
        boundaryTick
    ])

    return live
}
